import { type ReactNode, useEffect, useRef } from "react";
import { useRouter } from "solito/router";
import { Building2 as BuildingIcon } from "@tamagui/lucide-icons";
import { Badge, TextSpan, XStack, YStack } from "@medbillai/ui";
import { accountsLinkSelectUrl, flaggedBillsUrl, issueUrl } from "@medbillai/utils";
import { formatCurrency } from "@medbillai/utils/currency";
import { analyticsService } from "../../../lib/analytics";
import { usePushGetHelp } from "../../../lib/usePushGetHelp";
import { type CtaGraphic, CtaHeroCard, type CtaHeroCardProps } from "./CtaHeroCard";
import { CtaSecondaryTile } from "./CtaSecondaryTile";
import { type CtaLayoutInput, type CtaPrimary, resolveCtaLayout, type SecondaryCtaType } from "./resolveCtaLayout";
const CTA_CONTENT = {
  billHelp: {
    caseType: "BILL_ISSUE_V2" as const,
    title: "Get help with a bill",
    heroDescription: "Get clear, personalized answers in seconds.",
    secondaryDescription: "We'll find errors, overcharges, and savings.",
    buttonLabel: "Check now",
    source: "home_cta_bill",
    graphic: {
      type: "bill",
      indicator: "check"
    } satisfies CtaGraphic
  },
  billMonitoring: {
    // Monitoring routes to insurance linking (see ctaPress), not a case intake.
    caseType: "BILL_ISSUE_V2" as const,
    title: "Setup bill monitoring",
    heroDescription: "We'll monitor every claim, flagging issues",
    secondaryDescription: "We'll monitor every claim, flagging issues",
    buttonLabel: "Get started",
    source: "home_cta_monitoring",
    graphic: {
      type: "claim-check"
    } satisfies CtaGraphic
  },
  coverage: {
    caseType: "BENEFITS_QUESTION" as const,
    title: "Ask a coverage question",
    heroDescription: "Get clear, personalized answers in seconds.",
    secondaryDescription: "Get clear, personalized answers in seconds.",
    buttonLabel: "Ask now",
    source: "home_cta_coverage",
    graphic: {
      type: "coverage",
      indicator: "check"
    } satisfies CtaGraphic
  }
} satisfies Record<SecondaryCtaType, unknown>;
type GetHelpCtaType = keyof typeof CTA_CONTENT;

// Rendered inside CtaHeroCard's description Body2 — its own block wrapper
// would nest p-in-p on web.
function SavedDescription({
  savings,
  onBill
}: {
  savings: number;
  onBill: boolean;
}) {
  return <>
      We saved you{" "}
      <TextSpan highlight bold bg="$bgBrandStrong" color="$textDefault" data-sentry-element="TextSpan" data-sentry-source-file="PrimaryCtaZone.tsx">
        {formatCurrency(savings, "USD", {
        maximumFractionDigits: 0
      })}
      </TextSpan>
      {onBill ? " on your bill 🎉" : " 🎉"}
    </>;
}
type HeroHandlers = {
  push: (url: string) => void;
  ctaPress: (type: GetHelpCtaType) => () => void;
};
function primaryHeroProps(primary: CtaPrimary, {
  push,
  ctaPress
}: HeroHandlers): CtaHeroCardProps {
  switch (primary.type) {
    case "newClaims":
      return {
        testID: "home.new-claims-hero",
        title: "New claims detected",
        titleBadge: <Badge status="warning">{String(primary.count)}</Badge>,
        description: "Let Granted check them for errors and overcharges.",
        buttonLabel: "Review claims",
        graphic: {
          type: "claim-check",
          indicator: "plus"
        },
        onPress: () => {
          analyticsService.trackEvent("ClaimsReviewTapped", {
            action: "REVIEW_NOW"
          });
          push(flaggedBillsUrl());
        }
      };
    case "closedCase":
      {
        const {
          issueId,
          bucket,
          caseType,
          providerName,
          savings
        } = primary;
        const savedAmount = bucket === "saved" && savings != null && savings > 0 ? savings : null;
        const isBillCase = caseType === "BILL_ISSUE";
        return {
          testID: "home.closed-win-hero",
          title: "Congratulations!",
          graphic: {
            type: isBillCase ? "bill" : "coverage",
            indicator: savedAmount ? "dollar" : "check"
          },
          titleBadge: providerName ? <Badge status="neutral" iconLeft={BuildingIcon} size="small" $gtXs={{
            size: "medium"
          }}>
            {providerName}
          </Badge> : undefined,
          description: savedAmount ? <SavedDescription savings={savedAmount} onBill={isBillCase} /> : isBillCase ? "We reviewed your bill — no overcharges found." : "Your case is resolved — see how it went.",
          buttonLabel: "Review case",
          onPress: () => {
            analyticsService.trackEvent("OpenedIssue", {
              id: issueId,
              source: "home_closed_win_hero_review"
            });
            push(issueUrl({
              issueId
            }));
          }
        };
      }
    case "billHelp":
    case "billMonitoring":
      {
        const cta = CTA_CONTENT[primary.type];
        return {
          testID: `home.primary-cta.${primary.type}`,
          title: cta.title,
          description: cta.heroDescription,
          buttonLabel: cta.buttonLabel,
          onPress: ctaPress(primary.type),
          graphic: cta.graphic
        };
      }
  }
}
export type PrimaryCtaZoneProps = {
  layout: CtaLayoutInput;
  loading?: boolean;
};

// Below this width a tile wraps its title; when two of these plus the gap
// don't fit the row, the second tile wraps to its own full-width line.
const MIN_TILE_WIDTH = 360;
function TileSlot({
  children
}: {
  children: ReactNode;
}) {
  return <YStack flexGrow={1} flexBasis={MIN_TILE_WIDTH} minWidth={0} data-sentry-element="YStack" data-sentry-component="TileSlot" data-sentry-source-file="PrimaryCtaZone.tsx">
      {children}
    </YStack>;
}
const SKELETON_TILE_KEYS = ["tile-0", "tile-1"] as const;
export function PrimaryCtaZone({
  layout,
  loading = false
}: PrimaryCtaZoneProps) {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const {
    push
  } = useRouter();
  const {
    primary,
    secondary
  } = resolveCtaLayout(layout);
  const ctaViewedFired = useRef(false);
  useEffect(() => {
    if (loading || ctaViewedFired.current) {
      return;
    }
    ctaViewedFired.current = true;
    analyticsService.trackEvent("HomeCtaZoneViewed", {
      primary: primary?.type ?? "none",
      secondary
    });
  }, [loading, primary, secondary]);
  const seedIntake = (type: GetHelpCtaType) => () => {
    const cta = CTA_CONTENT[type];
    pushGetHelp({
      caseType: cta.caseType,
      title: cta.title,
      source: cta.source
    });
  };

  // "Set up monitoring" means link insurance so Granted can watch claims — it
  // opens the insurance-link flow rather than seeding a case intake.
  const ctaPress = (type: GetHelpCtaType) => type === "billMonitoring" ? () => push(accountsLinkSelectUrl({
    placeFrom: "insuranceHome",
    linkType: "all"
  })) : seedIntake(type);
  return <YStack gap="$sp1.5" aria-busy={loading || undefined} data-sentry-element="YStack" data-sentry-component="PrimaryCtaZone" data-sentry-source-file="PrimaryCtaZone.tsx">
      {loading ? <CtaHeroCard loading testID="home.primary-cta.skeleton" /> : primary ? <CtaHeroCard {...primaryHeroProps(primary, {
      push,
      ctaPress
    })} /> : null}
      <XStack gap="$sp1.5" flexWrap="wrap" alignItems="stretch" data-sentry-element="XStack" data-sentry-source-file="PrimaryCtaZone.tsx">
        {loading ? SKELETON_TILE_KEYS.map(key => <TileSlot key={key}>
                <CtaSecondaryTile loading testID={`home.secondary-cta.skeleton-${key}`} />
              </TileSlot>) : secondary.map(type => {
        const cta = CTA_CONTENT[type];
        return <TileSlot key={type}>
                  <CtaSecondaryTile testID={`home.secondary-cta.${type}`} title={cta.title} description={cta.secondaryDescription} onPress={ctaPress(type)} graphic={cta.graphic} />
                </TileSlot>;
      })}
      </XStack>
    </YStack>;
}